import { getJobsJobFileUrl, getJobsListJobsQueryKey, useJobsJob, useJobsListJobs } from "@api/generated/jobs/jobs";
import type { JobSummary, JobSummaryStatus } from "@api/generated/model";
import { Button } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { LoadingBar } from "@ui/LoadingBar";
import { QueryFeedback } from "@ui/QueryFeedback";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

export const isActiveJob = (j: JobSummary) => j.status === "running" || j.status === "queued";

const TONE: Record<JobSummaryStatus, string> = { queued: "", running: "g", done: "g", failed: "r", cancelled: "" };

export function JobStatusChip({ status, extra }: { status: JobSummaryStatus; extra?: string }) {
  const { t } = useTranslation();
  return (
    <span className={`chip ${TONE[status]}`}>
      {t(`jobs.statuses.${status}`)}
      {extra ? ` · ${extra}` : ""}
    </span>
  );
}

/** The download button of a finished job's result file, or nothing. */
export function JobFileButton({ job, label, size = "compact-xs" }: { job: JobSummary; label?: string; size?: "compact-xs" | "sm" }) {
  const { t } = useTranslation();
  if (!job.file_name || job.status !== "done") return null;
  return (
    <Button
      component="a"
      href={getJobsJobFileUrl(job.id)}
      size={size}
      variant={size === "sm" ? "filled" : "default"}
      onClick={(e) => e.stopPropagation()}
    >
      {label ?? t("jobs.download")}
    </Button>
  );
}

/** Jobs of the user, polled quickly only while one of them runs (the top bar badge and the home card share it). */
export function useMyJobs(limit = 20) {
  return useJobsListJobs({ limit }, { query: { refetchInterval: (q) => (q.state.data?.some(isActiveJob) ? 3000 : 30000) } });
}

/** A job this page started: ``watch(id)`` after the start request; ``job`` is then polled every second until it ends. */
export function useWatchedJob() {
  const client = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);
  const query = useJobsJob(jobId ?? "", {
    query: { enabled: !!jobId, refetchInterval: (q) => (q.state.data && !isActiveJob(q.state.data.job) ? false : 1000) },
  });
  const watch = useCallback(
    (id: string | null) => {
      setJobId(id);
      if (id) void client.invalidateQueries({ queryKey: getJobsListJobsQueryKey() });
    },
    [client],
  );
  return { jobId, job: query.data?.job, error: query.error, retry: query.refetch, watch };
}

/** Job progress stays on the originating screen, including polling failures. */
export function WatchedJobProgress({
  jobId,
  job,
  error,
  retry,
}: {
  jobId: string | null;
  job?: JobSummary;
  error: unknown;
  retry: () => unknown;
}) {
  const { t } = useTranslation();
  if (!jobId) return null;
  return (
    <div className="stack" style={{ gap: 6 }}>
      <QueryFeedback busy={!job && !error} error={error} retry={retry} />
      {Boolean(error) && (
        <p className="sm mu" role="status">
          {t("async.jobConnection")}
        </p>
      )}
      {job && (
        <>
          <div className="hstack sm" style={{ gap: 8 }}>
            <JobStatusChip status={job.status} />
            <span className="grow">{job.message || job.title}</span>
            {job.status === "running" && <span className="mono">{job.progress}%</span>}
            <JobFileButton job={job} />
          </div>
          <LoadingBar active={isActiveJob(job)} value={job.status === "running" ? job.progress : undefined} label={job.title} />
          {job.status === "failed" && job.error && (
            <p className="note err sm" role="alert">
              {job.error}
            </p>
          )}
        </>
      )}
    </div>
  );
}
